import type { QueryClient } from '@tanstack/react-query';

import { trackEvent } from '../analytics/posthog';
import { logger } from '../lib/logger';
import { fetchSession, sessionKeys } from './session-api';
import {
  clearSessionToken,
  getSessionToken,
  setSessionToken,
} from './session-token';

const log = logger.child({ module: 'extension-session' });

/**
 * Signs canvas in with the session the Floto Chrome extension
 * (`floto-chrome-extension`, a separate repo) already holds.
 *
 * The reverse of `useExtensionAuthHandoff`: there canvas hands a session *to*
 * an extension, here canvas *pulls* one from it. It works without any
 * exchange because both mint the same thing — a Firebase session cookie from
 * the same Firebase project — so `AuthService.verifySession` accepts the
 * extension's token as-is.
 *
 *   extension  --chrome.tabs.create(`/auth/extension?extId=&redirect=`)-->
 *   canvas     --chrome.runtime.sendMessage(extId, GET_CANVAS_SESSION)-->
 *   extension  --{ sessionToken } (checks sender origin + path + extId)-->
 *   canvas     --GET /auth/session, keep the token only if it verifies
 *
 * Pulled rather than put in the URL, which would leave a long-lived token in
 * history and server logs. The page must still be on `/auth/extension` while
 * messaging: Chrome checks `externally_connectable.matches` against the
 * document's current URL.
 *
 * Resolves whether canvas ends up signed in. An existing canvas session wins
 * over the extension's — whoever is signed in here stays signed in.
 *
 * Every way out is reported (`extension_handoff_succeeded` / `_failed` with a
 * `reason`). The failures used to reach only `log.warn`, which in production
 * is the user's own console — so a drop-off here looked identical whichever
 * of the five things went wrong.
 */
export async function adoptExtensionSession(
  queryClient: QueryClient,
  extId: string | null,
): Promise<boolean> {
  const startedAt = Date.now();
  const elapsed = () => Date.now() - startedAt;

  if (getSessionToken() && (await verifyStoredSession(queryClient))) {
    trackEvent('extension_handoff_succeeded', {
      duration_ms: elapsed(),
      via: 'canvas_session',
    });
    return true;
  }

  const answer = await requestExtensionSession(extId);
  if ('reason' in answer) {
    trackEvent('extension_handoff_failed', {
      attempts: answer.attempts,
      duration_ms: elapsed(),
      reason: answer.reason,
    });
    return false;
  }

  setSessionToken(answer.token);
  if (await verifyStoredSession(queryClient)) {
    trackEvent('extension_handoff_succeeded', {
      attempts: answer.attempts,
      duration_ms: elapsed(),
      via: 'extension',
    });
    return true;
  }

  // Expired or revoked on the extension's side — not worth keeping, and the
  // login page would otherwise start from a token that already failed.
  clearSessionToken();
  log.warn('the extension handed over a session the API rejected');
  trackEvent('extension_handoff_failed', {
    attempts: answer.attempts,
    duration_ms: elapsed(),
    reason: 'token_rejected',
  });
  return false;
}

async function verifyStoredSession(queryClient: QueryClient): Promise<boolean> {
  try {
    /**
     * Not `fetchQuery`: `useAuth` is mounted app-wide and has usually already
     * fired `GET /auth/session` *without* a token by the time one is stored.
     * `fetchQuery` joins that in-flight request instead of starting a new one,
     * so it answers "signed out" and a good token gets thrown away. Cancel it,
     * ask fresh with the token, and write the answer into the cache ourselves.
     */
    await queryClient.cancelQueries({ queryKey: sessionKeys.all });
    const session = await fetchSession();
    queryClient.setQueryData(sessionKeys.all, session);
    return session.authenticated;
  } catch {
    return false;
  }
}

/** Chrome extension ids are 32 letters a–p. Anything else is not worth a message. */
const EXTENSION_ID = /^[a-p]{32}$/;

/** The Floto Chrome extension's Chrome Web Store id. */
export const PROD_FLOTO_EXTENSION_ID = 'gnkckbifdpiibmmopohgdbjnfccghccc';

/**
 * Which extension production will take a session from: only the published
 * one, so a link  naming some other installed extension cannot sign canvas in
 * as whoever that extension says. Other deployments take any well-formed id —
 * a local unpacked build's id comes from its load path, so there is no list
 * worth keeping there.
 */
export function isAllowedSessionSource(
  extId: string | null,
  environment: string | undefined = import.meta.env.VITE_ENVIRONMENT as
    string | undefined,
): extId is string {
  if (!extId || !EXTENSION_ID.test(extId)) return false;
  return environment !== 'production' || extId === PROD_FLOTO_EXTENSION_ID;
}

/** Covers an MV3 service worker waking up, same as `useExtensionAuthHandoff`. */
const SEND_ATTEMPTS = 3;
const SEND_RETRY_MS = 400;

interface CanvasSessionResponse {
  success?: boolean;
  sessionToken?: string | null;
}

/**
 * Why the extension gave canvas no token. Values of `reason` on
 * `extension_handoff_failed` — `token_rejected` is the one added after this,
 * when a token arrived and the API turned it down.
 */
type HandoffRefusal =
  /** The `extId` is malformed, or not the published extension on production. */
  | 'refused_id'
  /** No `chrome.runtime.sendMessage`: not Chrome, or no extension platform. */
  | 'no_extension_api'
  /** Every attempt threw — usually an evicted service worker. */
  | 'unreachable'
  /** The extension answered, and is signed out. */
  | 'signed_out'
  /** The extension answered with a refusal (`success: false`). */
  | 'extension_refused';

type ExtensionAnswer =
  | { attempts: number; reason: HandoffRefusal }
  | { attempts: number; token: string };

async function requestExtensionSession(
  extId: string | null,
): Promise<ExtensionAnswer> {
  if (!isAllowedSessionSource(extId)) {
    log.warn({ extId }, 'refused to take a session from this extension id');
    return { attempts: 0, reason: 'refused_id' };
  }

  // `chrome` only exists on a browser with an extension platform.
  const runtime = (
    globalThis as { chrome?: { runtime?: { sendMessage?: unknown } } }
  ).chrome?.runtime;
  if (typeof runtime?.sendMessage !== 'function') {
    return { attempts: 0, reason: 'no_extension_api' };
  }

  const send = runtime.sendMessage as (
    id: string,
    message: unknown,
  ) => Promise<CanvasSessionResponse | undefined>;

  for (let attempt = 1; attempt <= SEND_ATTEMPTS; attempt += 1) {
    try {
      const response = await send(extId, { type: 'GET_CANVAS_SESSION' });
      // A refusal or a signed-out extension is an answer, not a dropped message.
      if (!response?.success) {
        return { attempts: attempt, reason: 'extension_refused' };
      }
      return typeof response.sessionToken === 'string'
        ? { attempts: attempt, token: response.sessionToken }
        : { attempts: attempt, reason: 'signed_out' };
    } catch (err) {
      if (attempt === SEND_ATTEMPTS) {
        log.warn(
          { attempts: attempt, err, extId },
          'could not reach the extension for its session',
        );
        return { attempts: attempt, reason: 'unreachable' };
      }
      await new Promise((resolve) =>
        setTimeout(resolve, SEND_RETRY_MS * attempt),
      );
    }
  }
  return { attempts: SEND_ATTEMPTS, reason: 'unreachable' };
}
